import { useMemo } from "react";

import { Container, Stack } from "@/components/ui";

import { useFilteredRoadmap } from "../../hooks";
import {
  AnalyticsHeader,
  KeyMetrics,
  StatusDistribution,
  TypeDistribution,
  ModuleBreakdown,
  AdditionalMetrics,
} from "./components";

/**
 * **Analytics Screen**
 *
 * Charts & metrics of the roadmap with various visualizations.
 * Shows statistics, trends and insights of roadmap items.
 * Refactored with TracQ branding and modular components.
 *
 * @returns {JSX.Element} Die gerenderte AnalyticsScreen-Komponente
 *
 */

export const AnalyticsScreen = () => {
  const items = useFilteredRoadmap();

  // Calculate analytics data
  const analytics = useMemo(() => {
    const total = items.length;
    const byStatus: Record<string, number> = {};
    for (const item of items) {
      byStatus[item.status] = (byStatus[item.status] ?? 0) + 1;
    }

    const byType: Record<string, number> = {};
    for (const item of items) {
      byType[item.type] = (byType[item.type] ?? 0) + 1;
    }

    const byModule: Record<string, number> = {};
    for (const item of items) {
      byModule[item.module] = (byModule[item.module] ?? 0) + 1;
    }

    const highPriority = items.filter((item) => item.priority === "high").length;
    const highImpact = items.filter((item) => item.impact_score === "high").length;
    const highEffort = items.filter((item) => item.effort_estimate === "high").length;

    return {
      byModule,
      byStatus,
      byType,
      completionRate: total > 0 ? ((byStatus.done ?? 0) / total) * 100 : 0,
      highEffort,
      highImpact,
      highPriority,
      total,
    };
  }, [items]);

  return (
    <Container className="text-white" maxWidth="full" padding="lg">
      <Stack direction="col" gap={6}>
        {/* Header */}
        <AnalyticsHeader />

        {/* Key Metrics */}
        <KeyMetrics
          completionRate={analytics.completionRate}
          highImpact={analytics.highImpact}
          highPriority={analytics.highPriority}
          total={analytics.total}
        />

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <StatusDistribution byStatus={analytics.byStatus} total={analytics.total} />
          <TypeDistribution byType={analytics.byType} total={analytics.total} />
        </div>

        {/* Module Breakdown */}
        <ModuleBreakdown byModule={analytics.byModule} total={analytics.total} />

        {/* Additional Metrics */}
        <AdditionalMetrics
          completed={analytics.byStatus.done ?? 0}
          highEffort={analytics.highEffort}
          inProgress={analytics.byStatus.in_progress ?? 0}
        />
      </Stack>
    </Container>
  );
};
